import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Icone } from '../components/Icone';
import { Chargement, MessageErreur, Pastille, PastilleSite } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre } from '../components/ui/Onglets';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { ErreurApi } from '../lib/api';
import { jourCivilReunion } from '../lib/dates-civiles';
import {
  useDashboardDirection,
  type ChoixPeriode,
  type DonneesDirection,
  type FiltreDirection,
} from '../lib/useDashboardDirection';
import { texteNps } from '../lib/useQuestionnaires';

type Filtre = Omit<FiltreDirection, 'site'>;

const SANS_FILTRE: Filtre = { periode: 'MOIS', du: '', au: '', formation: '', promotion: '' };

function jourFrancais(jour: string): string {
  const [annee, mois, j] = jour.split('-');
  return `${j}/${mois}/${annee}`;
}

function heureDeMiseAJour(instant: string): string {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

function taux(valeur: number | null): string {
  return valeur === null ? '—' : `${String(valeur).replace('.', ',')} %`;
}

/**
 * Page d'accueil de la direction (TF-425), sur la maquette validée
 * (`dashboardDirection`). Tout vient de `GET /api/dashboard/direction`, qui
 * résout la période et relit la base à chaque appel : l'écran ne compte rien.
 * Les chiffres se relisent seuls chaque minute et au retour sur l'onglet.
 * Les éléments de démonstration de la maquette — top formateurs, connexions
 * API des financeurs, jauge à 100 %, graphique d'activité — n'y sont pas.
 */
export function DashboardDirectionPage(): JSX.Element {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const { site } = usePerimetre();
  const [filtre, setFiltre] = useState<Filtre>(SANS_FILTRE);
  const { data, isLoading, isFetching, error } = useDashboardDirection({ site, ...filtre });

  const aujourdhui = new Date().toLocaleDateString('fr-FR', {
    timeZone: 'Indian/Reunion',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  // Une période personnalisée ne commence pas après aujourd'hui : l'API la refuse.
  const ceJour = jourCivilReunion(new Date());

  const choisirPeriode = (periode: ChoixPeriode): void =>
    setFiltre((avant) => ({
      ...avant,
      periode,
      // Une période personnalisée part de la période affichée.
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.au ?? '') : avant.au,
    }));

  const tous = !site || site === TOUS_LES_SITES;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <section className="gradient-card relative overflow-hidden rounded-2xl p-4 text-white sm:p-6 lg:p-8">
        <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-codialis-mint/10 blur-3xl" />
        <div className="relative flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div className="min-w-0">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-codialis-mint">
              Direction · {aujourdhui}
            </div>
            <h1 className="font-display mb-2 text-3xl font-extrabold lg:text-4xl">
              Bonjour {utilisateur?.prenom ?? ''}
            </h1>
            {data && <Resume donnees={data} />}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate('/ped-veille')}
              className="min-h-[44px] rounded-xl bg-codialis-mint px-5 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-teal"
            >
              Veille réglementaire
            </button>
            <button
              type="button"
              onClick={() => navigate('/invoicing')}
              className="min-h-[44px] rounded-xl border border-white/15 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
            >
              Factures
            </button>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <BarreSites />
          <ChampFiltre
            valeur={filtre.periode}
            onChange={(valeur) => choisirPeriode(valeur as ChoixPeriode)}
            etiquette="Période"
          >
            {(data?.filtres.periodes ?? [{ valeur: 'MOIS', libelle: 'Mois en cours' }]).map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          {filtre.periode === 'LIBRE' && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <ChampDate
                libelle="Du"
                valeur={filtre.du}
                max={filtre.au && filtre.au < ceJour ? filtre.au : ceJour}
                onChange={(du) => setFiltre((avant) => ({ ...avant, du }))}
              />
              <ChampDate
                libelle="au"
                valeur={filtre.au}
                min={filtre.du || undefined}
                onChange={(au) => setFiltre((avant) => ({ ...avant, au }))}
              />
            </div>
          )}
          <ChampFiltre
            valeur={filtre.formation}
            onChange={(formation) => setFiltre((avant) => ({ ...avant, formation, promotion: '' }))}
            etiquette="Formation"
          >
            <option value="">Toutes les formations</option>
            {data?.filtres.formations.map((formation) => (
              <option key={formation.code} value={formation.code}>
                {formation.titre}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre
            valeur={filtre.promotion}
            onChange={(promotion) => setFiltre((avant) => ({ ...avant, promotion }))}
            etiquette="Promotion"
          >
            <option value="">Toutes les promotions</option>
            {data?.filtres.promotions.map((promotion) => (
              <option key={promotion.cle} value={promotion.cle}>
                {promotion.libelle} · {promotion.siteCode}
              </option>
            ))}
          </ChampFiltre>
        </div>
        {data && (
          <p className="text-xs text-codialis-gray" aria-live="polite">
            <span className="font-semibold text-codialis-navy">Période : {data.periode.libelle}</span>
            {' · '}
            {isFetching ? 'Actualisation…' : `Mis à jour à ${heureDeMiseAJour(data.calculeLe)}`}
          </p>
        )}
      </div>

      {filtre.periode === 'LIBRE' && filtre.du && filtre.au && filtre.du > filtre.au && (
        <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
      )}
      {isLoading && <Chargement libelle="Chargement du tableau de bord…" />}
      {!isLoading && error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'Impossible de charger le tableau de bord.'}
        />
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Indicateur
              libelle="Apprenants actifs"
              valeur={String(data.actifs.nombre)}
              detail={data.actifs.detail}
              icone="utilisateurs"
              couleur="sky"
            />
            <Indicateur
              libelle="Heures formées"
              valeur={data.heures.texte}
              detail={data.heures.detail}
              icone="calendrier"
              couleur="mint"
            />
            <Indicateur
              libelle="Chiffre d’affaires HT"
              valeur={data.chiffreAffaires.netHt.texte}
              detail={data.chiffreAffaires.detail}
              icone="euro"
              couleur="amber"
            />
            <Indicateur
              libelle="Satisfaction"
              valeur={data.satisfaction.valeur}
              detail={data.satisfaction.detail}
              icone="etoile"
              couleur="mint"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <CarteQualiopi
                qualiopi={data.qualiopi}
                onVeille={() => navigate('/ped-veille')}
                onIndicateurs={() => navigate('/qualiopi')}
              />

              <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
                <h2 className="font-display text-lg font-bold text-codialis-navy">Pipeline opérationnel</h2>
                <p className="mb-4 text-sm text-codialis-gray">
                  Ce qui attend les pôles aujourd’hui{tous ? ' · toutes antennes' : ` · ${site}`}, hors filtres de
                  période, de formation et de promotion
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {data.pipeline.map((ligne) => (
                    <button
                      key={ligne.id}
                      type="button"
                      onClick={() => navigate(ligne.page)}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-gray-100 p-3 text-left transition hover:border-codialis-mint/40 hover:bg-gray-50"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-codialis-navy">{ligne.libelle}</span>
                        <span className="block text-[11px] text-codialis-gray">{ligne.detail}</span>
                      </span>
                      <span className={`badge-${ligne.ton} shrink-0 rounded px-2 py-0.5 text-xs font-bold`}>
                        {ligne.valeur}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <TableauAntennes antennes={data.antennes} periode={data.periode.libelle} />
            </div>

            <div className="min-w-0 space-y-6">
              <CarteSynthese
                synthese={data.derniereSynthese}
                onOuvrir={() =>
                  navigate(
                    data.derniereSynthese ? `/dir-syntheses?synthese=${data.derniereSynthese.id}` : '/dir-syntheses',
                  )
                }
              />
              <CarteChiffre chiffre={data.chiffreAffaires} onFactures={() => navigate('/invoicing')} />
              <CarteSatisfaction
                satisfaction={data.satisfaction}
                onQuestionnaires={() => navigate('/satisfaction?onglet=tableau')}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Resume({ donnees }: { donnees: DonneesDirection }): JSX.Element {
  const aEmettre = donnees.pipeline.find((ligne) => ligne.id === 'a-emettre')?.valeur ?? 0;
  const qualiopi =
    donnees.qualiopi.etat === 'CERTIFIE'
      ? 'certifiée dans la liste publique'
      : donnees.qualiopi.etat === 'A_VERIFIER'
        ? 'à vérifier'
        : 'pas encore relevée';
  return (
    <p className="max-w-2xl text-white/70">
      Votre centre compte{' '}
      <strong className="text-white">{pluriel(donnees.actifs.aujourdHui, 'apprenant actif', 'apprenants actifs')}</strong>,
      la certification Qualiopi est <strong className="text-codialis-mint">{qualiopi}</strong>
      {aEmettre > 0 ? (
        <>
          , et <strong className="text-white">{pluriel(aEmettre, 'facture', 'factures')}</strong>{' '}
          {aEmettre > 1 ? 'sont' : 'est'} à émettre.
        </>
      ) : (
        '.'
      )}
    </p>
  );
}

const COULEURS_INDICATEUR = {
  sky: { fond: 'bg-codialis-sky/10 text-codialis-sky', detail: 'text-codialis-sky' },
  mint: { fond: 'bg-codialis-mint/10 text-codialis-mint-clair', detail: 'text-codialis-mint-clair' },
  amber: { fond: 'bg-amber-100 text-amber-600', detail: 'text-amber-600' },
} as const;

function Indicateur({
  libelle,
  valeur,
  detail,
  icone,
  couleur,
}: {
  libelle: string;
  valeur: string;
  detail: string;
  icone: 'utilisateurs' | 'calendrier' | 'euro' | 'etoile';
  couleur: keyof typeof COULEURS_INDICATEUR;
}): JSX.Element {
  return (
    <div className="stat-card min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${COULEURS_INDICATEUR[couleur].fond}`}>
        <Icone nom={icone} className="h-5 w-5" />
      </div>
      <div className="text-xs font-medium uppercase tracking-wide text-codialis-gray">{libelle}</div>
      <div className="font-display mt-1 break-words text-3xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className={`mt-1 text-xs font-medium ${COULEURS_INDICATEUR[couleur].detail}`}>{detail}</div>
    </div>
  );
}

function CarteQualiopi({
  qualiopi,
  onVeille,
  onIndicateurs,
}: {
  qualiopi: DonneesDirection['qualiopi'];
  onVeille: () => void;
  onIndicateurs: () => void;
}): JSX.Element {
  const { conformite } = qualiopi;
  const releves = qualiopi.organismes
    .map((organisme) => organisme.releveLe)
    .filter((jour): jour is string => jour !== null)
    .sort();
  const dernierReleve = releves[releves.length - 1] ?? null;
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-codialis-navy">Conformité &amp; audit</h2>
          <p className="text-sm text-codialis-gray">
            {qualiopi.echeance
              ? `${qualiopi.echeance.nature} : ${jourFrancais(qualiopi.echeance.date)} · ${qualiopi.echeance.intitule}`
              : 'Aucune échéance de certification saisie dans la veille réglementaire'}
          </p>
        </div>
        <span className="qualiopi-badge shrink-0 rounded px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
          Qualiopi
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl bg-gradient-to-br from-codialis-mint/10 to-emerald-50 p-4 text-center">
          <div className="font-display text-2xl font-extrabold text-codialis-teal">{qualiopi.libelle}</div>
          <div className="mt-1 text-xs text-codialis-gray">
            {dernierReleve ? `Liste publique · relevé du ${jourFrancais(dernierReleve)}` : 'Liste publique des OF'}
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-codialis-sky/10 to-blue-50 p-4 text-center">
          <div className="font-display text-3xl font-extrabold text-codialis-sky">{qualiopi.evolutionsAAnalyser}</div>
          <div className="mt-1 text-xs text-codialis-gray">
            {qualiopi.evolutionsAAnalyser > 1 ? 'Évolutions Qualiopi à analyser' : 'Évolution Qualiopi à analyser'}
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-amber-100 to-orange-50 p-4 text-center">
          <div className="font-display text-3xl font-extrabold text-amber-600">
            {qualiopi.echeance ? (qualiopi.echeance.jours < 0 ? 'Dépassée' : `${qualiopi.echeance.jours} j`) : '—'}
          </div>
          <div className="mt-1 text-xs text-codialis-gray">Avant l’échéance de la certification</div>
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {qualiopi.organismes.map((organisme) => (
          <li
            key={organisme.numeroDeclaration ?? organisme.antennes.join('-')}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 p-3"
          >
            {organisme.antennes.map((antenne) => (
              <PastilleSite key={antenne} site={antenne} />
            ))}
            {/* Au téléphone, le texte passe sous les pastilles plutôt que d'y être tassé. */}
            <span className="order-last w-full text-sm text-codialis-navy sm:order-none sm:w-auto sm:min-w-0 sm:flex-1">
              {organisme.numeroDeclaration && (
                <span className="font-mono text-xs text-codialis-gray">NDA {organisme.numeroDeclaration} · </span>
              )}
              {organisme.detail}
            </span>
            <span className="ml-auto sm:ml-0">
              <Pastille
                texte={organisme.certifie ? 'Certifié' : organisme.releveLe ? 'À vérifier' : 'Non relevé'}
                ton={organisme.certifie ? 'success' : organisme.releveLe ? 'danger' : 'neutral'}
              />
            </span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onIndicateurs}
        className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-gray-100 p-3 text-left transition hover:bg-gray-50"
      >
        <span className="min-w-0 text-sm text-codialis-navy">
          <span className="font-semibold">
            Indicateurs : {conformite.conformes} conforme{conformite.conformes > 1 ? 's' : ''} sur{' '}
            {conformite.applicables} applicable{conformite.applicables > 1 ? 's' : ''}
          </span>
          {conformite.aCompleter > 0 && (
            <span className="text-codialis-gray"> · {conformite.aCompleter} à compléter</span>
          )}
          {conformite.nonApplicables > 0 && (
            <span className="text-codialis-gray"> · {conformite.nonApplicables} non applicable{conformite.nonApplicables > 1 ? 's' : ''}</span>
          )}
        </span>
        <Pastille
          texte={conformite.aCompleter === 0 ? 'Complet' : 'À compléter'}
          ton={conformite.aCompleter === 0 ? 'success' : 'warning'}
        />
      </button>
      <p className="mt-3 text-xs text-codialis-gray">
        La certification porte sur l’organisme : les filtres de période, de formation et de promotion ne s’y
        appliquent pas. Les indicateurs sont lus sur l’antenne choisie.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={onIndicateurs}
          className="min-h-[44px] w-full rounded-xl bg-codialis-navy py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
        >
          Voir les {conformite.indicateurs} indicateurs
        </button>
        <button
          type="button"
          onClick={onVeille}
          className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
        >
          Voir la veille réglementaire
        </button>
      </div>
    </div>
  );
}

function TableauAntennes({
  antennes,
  periode,
}: {
  antennes: DonneesDirection['antennes'];
  periode: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display text-lg font-bold text-codialis-navy">Pilotage multi-sites · Répartition par antenne</h2>
      <p className="mb-3 text-sm text-codialis-gray">
        Apprenants actifs, entrées, heures, présence et chiffre d’affaires sur {periode}
      </p>
      {/* Au téléphone, une fiche par antenne plutôt qu'un tableau à faire défiler. */}
      <ul className="space-y-3 sm:hidden">
        {antennes.map((antenne) => (
          <li key={antenne.code} className="rounded-xl border border-gray-100 p-3">
            <div className="mb-2 flex min-w-0 items-center gap-2 font-semibold text-codialis-navy">
              <PastilleSite site={antenne.code} />
              <span className="truncate">{antenne.nom}</span>
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-codialis-gray">Actifs</dt>
              <dd className="text-right font-mono text-codialis-navy">{antenne.actifs}</dd>
              <dt className="text-codialis-gray">Entrées</dt>
              <dd className="text-right font-mono text-codialis-navy">{antenne.entres}</dd>
              <dt className="text-codialis-gray">Heures formées</dt>
              <dd className="text-right text-codialis-navy">{antenne.heures}</dd>
              <dt className="text-codialis-gray">Présence</dt>
              <dd className="text-right font-bold text-codialis-teal">{taux(antenne.tauxPresence)}</dd>
              <dt className="text-codialis-gray">CA HT</dt>
              <dd className="text-right font-semibold text-codialis-navy">{antenne.chiffreAffaires.texte}</dd>
            </dl>
          </li>
        ))}
        {antennes.length === 0 && (
          <li className="text-center text-xs text-codialis-gray">Aucune antenne dans votre périmètre.</li>
        )}
      </ul>
      <div className="relative hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
            <tr>
              <th className="px-3 py-2.5 text-left font-bold">Antenne</th>
              <th className="px-3 py-2.5 text-right font-bold">Actifs</th>
              <th className="px-3 py-2.5 text-right font-bold">Entrées</th>
              <th className="px-3 py-2.5 text-right font-bold">Heures formées</th>
              <th className="px-3 py-2.5 text-right font-bold">Présence</th>
              <th className="px-3 py-2.5 text-right font-bold">CA HT</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {antennes.map((antenne) => (
              <tr key={antenne.code} className="hover:bg-gray-50">
                <td className="px-3 py-3 font-semibold text-codialis-navy">
                  <span className="flex min-w-0 items-center gap-2">
                    <PastilleSite site={antenne.code} />
                    <span className="truncate">{antenne.nom}</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-codialis-navy">{antenne.actifs}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-codialis-navy">{antenne.entres}</td>
                <td className="px-3 py-3 text-right text-xs text-codialis-gray">{antenne.heures}</td>
                <td className="px-3 py-3 text-right font-bold text-codialis-teal">{taux(antenne.tauxPresence)}</td>
                <td className="whitespace-nowrap px-3 py-3 text-right text-xs font-semibold text-codialis-navy">
                  {antenne.chiffreAffaires.texte}
                </td>
              </tr>
            ))}
            {antennes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-xs text-codialis-gray">
                  Aucune antenne dans votre périmètre.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LigneMontant({
  libelle,
  valeur,
  fort = false,
}: {
  libelle: string;
  valeur: string;
  fort?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className={`min-w-0 text-sm ${fort ? 'font-semibold text-codialis-navy' : 'text-codialis-gray'}`}>{libelle}</dt>
      <dd className={`whitespace-nowrap text-sm ${fort ? 'font-display text-lg font-extrabold' : 'font-semibold'} text-codialis-navy`}>
        {valeur}
      </dd>
    </div>
  );
}

function CarteChiffre({
  chiffre,
  onFactures,
}: {
  chiffre: DonneesDirection['chiffreAffaires'];
  onFactures: () => void;
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display text-lg font-bold text-codialis-navy">Chiffre d’affaires</h2>
      <p className="text-sm text-codialis-gray">Factures et avoirs émis sur la période, hors taxes</p>
      <dl className="mt-3 divide-y divide-gray-100">
        <LigneMontant libelle={`Facturé · ${pluriel(chiffre.factures, 'facture')}`} valeur={chiffre.facturesHt.texte} />
        <LigneMontant
          libelle={`Avoirs · ${pluriel(chiffre.avoirs, 'avoir')}`}
          valeur={chiffre.avoirsHt.centimes === 0 ? chiffre.avoirsHt.texte : `− ${chiffre.avoirsHt.texte}`}
        />
        <LigneMontant libelle="Chiffre d’affaires HT" valeur={chiffre.netHt.texte} fort />
        <LigneMontant
          libelle={`Encaissé sur la période · ${pluriel(chiffre.reglements, 'règlement')}`}
          valeur={chiffre.encaisse.texte}
        />
        <LigneMontant
          libelle={`Reste dû sur ces factures · ${pluriel(chiffre.resteDu.factures, 'facture')}`}
          valeur={chiffre.resteDu.texte}
        />
      </dl>
      <button
        type="button"
        onClick={onFactures}
        className="mt-4 min-h-[44px] w-full rounded-xl bg-codialis-navy py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
      >
        Voir les factures
      </button>
    </div>
  );
}

function CarteSatisfaction({
  satisfaction,
  onQuestionnaires,
}: {
  satisfaction: DonneesDirection['satisfaction'];
  onQuestionnaires: () => void;
}): JSX.Element {
  const { recommandation, nps } = satisfaction;
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display text-lg font-bold text-codialis-navy">Satisfaction</h2>
      <p className="text-sm text-codialis-gray">Questionnaires de satisfaction répondus sur la période</p>
      <dl className="mt-3 divide-y divide-gray-100">
        <LigneMontant libelle="Note moyenne" valeur={satisfaction.valeur} fort />
        <LigneMontant
          libelle="NPS"
          valeur={nps.score === null ? '—' : `${texteNps(nps.score)} (${nps.repondants} note${nps.repondants > 1 ? 's' : ''})`}
          fort
        />
        {nps.parts && (
          <LigneMontant
            libelle="Promoteurs · passifs · détracteurs"
            valeur={`${nps.parts.promoteurs} % · ${nps.parts.passifs} % · ${nps.parts.detracteurs} %`}
          />
        )}
        {/* L'ancienne question Oui/Non, que gardent les questionnaires envoyés avant le NPS. */}
        {recommandation.repondants > 0 && (
          <LigneMontant
            libelle="Recommanderaient (ancienne question)"
            valeur={`${recommandation.part} % (${recommandation.oui}/${recommandation.repondants})`}
          />
        )}
        <LigneMontant libelle="Réponses" valeur={String(satisfaction.reponses)} />
      </dl>
      <p className="mt-3 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-gray">
        Recommandation notée de 0 à 10 : part des promoteurs (9-10) moins part des détracteurs (0-6).
      </p>
      <button
        type="button"
        onClick={onQuestionnaires}
        className="mt-4 min-h-[44px] w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
      >
        Ouvrir le tableau de satisfaction
      </button>
    </div>
  );
}

/** La dernière synthèse mensuelle (TF-440) : produite le 1er, envoyée à la direction. */
function CarteSynthese({
  synthese,
  onOuvrir,
}: {
  synthese: DonneesDirection['derniereSynthese'];
  onOuvrir: () => void;
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display text-lg font-bold text-codialis-navy">Synthèse mensuelle</h2>
      {synthese ? (
        <>
          <p className="text-sm text-codialis-gray">
            {synthese.titre} · version {synthese.version}
          </p>
          <p className="mt-2 text-xs text-codialis-gray">
            {synthese.envoi
              ? `Envoyée le ${new Date(synthese.envoi.le).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })} à la direction.`
              : 'Pas encore envoyée.'}
          </p>
        </>
      ) : (
        <p className="text-sm text-codialis-gray">
          Activité, satisfaction, absentéisme, financeurs et impact économique du mois écoulé, produits le 1er du mois.
        </p>
      )}
      <button
        type="button"
        onClick={onOuvrir}
        className="mt-4 min-h-[44px] w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
      >
        {synthese ? 'Lire la synthèse' : 'Ouvrir les synthèses'}
      </button>
    </div>
  );
}

function ChampDate({
  libelle,
  valeur,
  min,
  max,
  onChange,
}: {
  libelle: string;
  valeur: string;
  min?: string | undefined;
  max?: string | undefined;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <label className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
      <span className="w-8 shrink-0 text-xs text-codialis-gray sm:w-auto">{libelle}</span>
      <input
        type="date"
        value={valeur}
        min={min}
        max={max}
        onChange={(evenement) => onChange(evenement.target.value)}
        className="min-h-[44px] w-full min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:w-40 sm:flex-none"
      />
    </label>
  );
}
